// app/src/modules/ui/admin.tsx
// Shared compact primitives for the unified Back Office: a primary section tab bar,
// stat cards, status badges, and a searchable / sortable / paginated data table.
// Styling matches the upgraded POS — compact type, restrained borders, minimal shadow.
// (Bootstrap) — layout via Bootstrap utilities; design tokens via inline CSS variables.
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EmptyState } from "./Loader";
import { buildReportHtml } from "../../lib/reportDoc";
import { openDocument } from "./DocumentViewer";
import UiBadge, { type BadgeTone as UiBadgeTone } from "./Badge";
import { DEFAULT_NAV } from "../../../../shared/nav";
import { useNav, navItemFor } from "../../lib/nav";
import type { NavSection } from "../../../../shared/nav";

/* ───────────────────── Tab icons ─────────────────────
   Stroke icon paths (24×24 viewBox) keyed to match the BO_NAV `icon` fields below
   (and the per-category `icon`). Rendered before each tab / sub-tab label. */
export const NAV_ICONS: Record<string, string> = {
  dashboard:    "M3 3h7v9H3z M14 3h7v5h-7z M14 12h7v9h-7z M3 16h7v5H3z",
  catalogue:    "M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z M3.3 7 12 12l8.7-5 M12 22V12",
  purchasing:   "M8 21a1 1 0 1 0 0-2 1 1 0 0 0 0 2z M19 21a1 1 0 1 0 0-2 1 1 0 0 0 0 2z M2 3h2.2l2.6 12.4a2 2 0 0 0 2 1.6h9.4a2 2 0 0 0 2-1.6L21.6 7H5.2",
  finance:      "M23 6l-9.5 9.5-5-5L1 18 M17 6h6v6",
  customers:    "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M22 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75",
  admin:        "M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z M12 2v3 M12 19v3 M4.2 6.6l2.2 1.3 M17.6 16.1l2.2 1.3 M19.8 6.6l-2.2 1.3 M6.4 16.1l-2.2 1.3",
  products:     "M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z M3.3 7 12 12l8.7-5 M12 22V12",
  kits:         "M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.8-3.8a6 6 0 0 1-7.9 7.9l-6.9 6.9a2.1 2.1 0 0 1-3-3l6.9-6.9a6 6 0 0 1 7.9-7.9l-3.8 3.8z",
  bikes:        "M3 5h11v11H3z M14 9h4l3 3v4h-3 M9 17h5 M6.5 17a2 2 0 1 1-.01 0z M18.5 17a2 2 0 1 1-.01 0z",
  waste:        "M3 6h18 M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2 M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6 M10 11v6 M14 11v6",
  orders:       "M22 12h-6l-2 3h-4l-2-3H2 M5.4 5.1 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.4-6.9A2 2 0 0 0 16.8 4H7.2a2 2 0 0 0-1.8 1.1z",
  transactions: "M5 3h14v18l-3-2-2 2-2-2-2 2-3-2V3z M9 8h6 M9 12h6",
  reports:      "M3 3v18h18 M18 17V9 M13 17V5 M8 17v-3",
  card:         "M2 5h20a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z M1 10h22 M6 15h4",
  accounts:     "M12 3v18 M3 7l9-3 9 3 M6 7l-3 7a3 3 0 0 0 6 0l-3-7z M18 7l-3 7a3 3 0 0 0 6 0l-3-7z",
  settings:     "M4 21v-7 M4 10V3 M12 21v-9 M12 8V3 M20 21v-5 M20 12V3 M1 14h6 M9 8h6 M17 16h6",
  staff:        "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M16 11l2 2 4-4",
  website:      "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M3 12h18 M12 3a13 13 0 0 1 0 18 13 13 0 0 1 0-18z",
  records:      "M12 8c4.4 0 8-1.3 8-3s-3.6-3-8-3-8 1.3-8 3 3.6 3 8 3z M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5 M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6",
  logs:         "M8 6h13 M8 12h13 M8 18h13 M3 6h.01 M3 12h.01 M3 18h.01",
  importer:     "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3",
  exporter:     "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12",
  audit:        "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z M9 12l2 2 4-4",
  shift:        "M2 7h20v12H2z M2 11h20 M6 15h4 M12 3v4",
  workshop:     "M7 17a2 2 0 1 1-4 0 2 2 0 0 1 4 0z M21 17a2 2 0 1 1-4 0 2 2 0 0 1 4 0z M5 17H3v-5l2-5h9l4 5h2a1 1 0 0 1 1 1v4h-2 M5 17h10",
  board:        "M4 4h16v16H4z M9 4v16 M4 10h16",
  diary:        "M3 4h18v18H3z M16 2v4 M8 2v4 M3 10h18",
  reminders:    "M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9 M10.3 21a1.94 1.94 0 0 0 3.4 0",
  glossary:     "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 16v-4 M12 8h.01",
};

export function TabIcon({ name, size = 15 }: { name?: string; size?: number }) {
  const d = name ? NAV_ICONS[name] : undefined;
  if (!d) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink: 0 }}>
      <path d={d} />
    </svg>
  );
}

/* ───────────────────── Back-office navigation (single source of truth) ─────────────────────
   One config drives BOTH the header tabs (categories + sub-tabs) and the Dashboard grid, so
   there is exactly one place to add/move/rename a back-office section. `icon` is a key into
   NAV_ICONS above. */
/** Built-in navigation. The live tree comes from /api/navigation (defaults +
 *  the admin's overrides) via useNav(); this remains the fallback used before
 *  that request lands and if it ever fails, so the app is never navigation-less. */
export const BO_NAV = DEFAULT_NAV;

export function SectionTabs() {
  const loc = useLocation();
  const nav = useNavigate();
  const sections = useNav();
  const path = loc.pathname;
  // Keep the active tab visible when the row overflows (phones/tablets).
  useEffect(() => {
    for (const el of document.querySelectorAll(".bo-tab.on, .bo-subtab.on")) {
      (el as HTMLElement).scrollIntoView({ inline: "center", block: "nearest", behavior: "instant" as ScrollBehavior });
    }
  }, [path]);
  const onDash = path === "/backoffice";
  const hit = onDash ? null : navItemFor(sections, path);
  const activeCat = hit?.cat ?? null;
  return (
    <>
      {/* One band, one row, sticky. It used to be up to three stacked rows —
          tabs, a back row, and a sub-tab row — none of them pinned, so the whole
          lot scrolled away and the page began a third of the way down. Back and
          the section menu now sit on the same line as the tabs, which is what
          makes the band a single fixed height and therefore stickable. */}
      <div className="bo-nav">
      {activeCat && (
        <button className="bo-back" onClick={() => {
          if (window.history.length > 1) void nav(-1);
          else void nav(activeCat.items[0].to);
        }} aria-label="Go back">
          <span aria-hidden>←</span> Back
        </button>
      )}
      <nav className="bo-tabs" aria-label="Back office sections">
        <button className={`bo-tab${onDash ? " on" : ""}`} aria-current={onDash ? "page" : undefined} onClick={() => nav("/backoffice")}><TabIcon name="dashboard" />Dashboard</button>
        {sections.map((c) => (
          <SectionTab key={c.label} cat={c} on={activeCat === c} activeTo={hit?.item?.to ?? null} onGo={nav} />
        ))}
      </nav>
      </div>
    </>
  );
}

/** One section tab. Sections with more than one page carry their own caret and
 *  open their own menu.
 *
 *  The pages used to be a second row of tabs — twelve on Admin, five on Finance —
 *  which on anything narrower than a laptop was a horizontal drag through a list
 *  you had to pull to read, and cost a whole band of height on every screen. My
 *  first attempt collapsed all of it into a single menu pinned to the right of
 *  the band, which fixed the height but put every section's pages behind one
 *  control that named none of them until you opened it.
 *
 *  A caret per section is better and is what the PK build already does: the
 *  pages sit under the section that owns them, a section with a single page has
 *  no caret and simply navigates, and nothing is hidden behind a control that
 *  belongs to something else. */
function SectionTab({ cat, on, activeTo, onGo }: {
  cat: NavSection; on: boolean; activeTo: string | null; onGo: (to: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);
  const btn = useRef<HTMLButtonElement | null>(null);
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);
  const many = cat.items.length > 1;

  /* The menu is positioned FIXED against the viewport, not absolutely inside the
     tab. .bo-tabs is a horizontal scroller with a mask so the row can scroll on a
     phone, and both of those clip an absolutely positioned descendant — the menu
     rendered at the right size, reported itself visible, and was invisible and
     unclickable because the scroller cut it away. Measured: 210x125, visibility
     visible, and elementFromPoint over it returned .bo-body.
     Anchoring off the button's own rect escapes the clip without giving up the
     scrolling row. Recomputed on open, and on scroll or resize while open, since
     a fixed element does not follow its anchor by itself. */
  useEffect(() => {
    if (!open) { setAt(null); return; }
    const place = () => {
      const r = btn.current?.getBoundingClientRect();
      if (!r) return;
      const width = 210;
      const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
      setAt({ left, top: r.bottom + 6 });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      const t = e.target as Node;
      // The menu lives on <body> now, so "outside the tab" is no longer the
      // right test — it would close on its own items before they fired.
      if (box.current?.contains(t)) return;
      if ((t as Element)?.closest?.(".bo-submenu-pop")) return;
      setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);

  if (!many) {
    return (
      <button className={`bo-tab${on ? " on" : ""}`} aria-current={on ? "page" : undefined}
        onClick={() => onGo(cat.items[0].to)}>
        <TabIcon name={cat.icon} />{cat.label}
      </button>
    );
  }
  return (
    <div className="bo-tabwrap" ref={box}>
      <button ref={btn} className={`bo-tab${on ? " on" : ""}`} aria-current={on ? "page" : undefined}
        aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <TabIcon name={cat.icon} />{cat.label}
        <span className="bo-tab-caret" aria-hidden>▾</span>
      </button>
      {open && at && createPortal(
        /* Portalled to <body>. Fixed positioning was not enough: .bo-tabs carries
           a mask-image so the scrolling row fades at its edges, and a masked
           ancestor becomes the containing block for position:fixed as well as
           position:absolute. The menu was therefore still trapped inside the
           scroller — it measured 210x125 and reported itself visible while
           elementFromPoint over it returned .bo-body, which is what "the
           dropdowns do not work" looked like from the outside. A portal leaves
           the subtree entirely; the coordinates above keep it under its tab. */
        <div className="bo-submenu-pop is-fixed" role="menu" aria-label={`${cat.label} pages`}
          style={{ left: at.left, top: at.top }}>
          {cat.items.map((it) => (
            <button key={it.to} role="menuitem"
              className={`bo-submenu-item${it.to === activeTo ? " on" : ""}`}
              aria-current={it.to === activeTo ? "page" : undefined}
              onClick={() => { setOpen(false); onGo(it.to); }}>
              <TabIcon name={it.icon} />{it.label}
            </button>
          ))}
        </div>, document.body)}
    </div>
  );
}


/* ───────────────────────── Stat cards ───────────────────────── */
type Tone = "default" | "good" | "warning" | "danger" | "accent";
const toneColor: Record<Tone, string> = {
  // Text tones use --accent-text (6.1:1 on the dark canvas); plain --accent is
  // 2.9:1 as text and is for fills. This map coloured every red stat value —
  // the 18 Sep contrast fix matched only literal `color:` keys and missed it.
  default: "var(--ink)", good: "var(--green)", warning: "var(--gold)", danger: "var(--accent-text)", accent: "var(--accent-text)",
};

export function StatGrid({ children, min = 132 }: { children: ReactNode; min?: number }) {
  return <div className="d-grid" style={{ minWidth: 0, gap: 10, gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))` }}>{children}</div>;
}

export function StatCard({ label, value, tone = "default", hint, onClick }: {
  label: string; value: ReactNode; tone?: Tone; hint?: string; onClick?: () => void;
}) {
  const body = (
    <>
      <span className="text-text-truncate text-text-uppercase fw-semibold" style={{ fontSize: 11, letterSpacing: ".03em", color: "var(--muted)" }}>{label}</span>
      <span className="num" style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-.01em", color: toneColor[tone] }}>{value}</span>
      {hint ? <span className="text-text-truncate t-xs-muted" >{hint}</span> : null}
    </>
  );
  const baseClass = "d-flex flex-column text-start";
  const baseStyle: CSSProperties = { gap: 2, borderRadius: "var(--r-md)", border: "1px solid var(--line)", background: "var(--card)", padding: "8px 12px" };
  return onClick
    ? <button onClick={onClick} className={`card-interactive ${baseClass}`} style={{ ...baseStyle, cursor: "pointer" }}>{body}</button>
    : <div className={baseClass} style={baseStyle}>{body}</div>;
}

/* ───────────────────────── Status badge ───────────────────────── */
/* ── Badge ────────────────────────────────────────────────────────────────────
   There were TWO badge components — this one and ui/Badge.tsx — with different
   shapes, different type treatment and overlapping tone names. That is the
   "random badges" problem at source: the same status could look like two
   different things depending on which page drew it. This is now a thin adapter
   over the single implementation in ui/Badge.tsx, so every badge in the app
   comes from one place.

   The hierarchy, one meaning per colour:
     neutral   a fact, no action implied            draft, each, cash
     info      a fact worth noticing                Website, Workshop, Trade
     success   done, healthy, settled               Paid, Received, In stock
     warning   needs a decision soon                Low stock, Awaiting payment
     danger    money or stock at risk, or blocked   Out of stock, Overdue, Void

   Fixed on the way through: `accent` and `danger` used to render IDENTICALLY,
   so "emphasised" and "wrong" were indistinguishable — the worst thing a status
   colour can do. `warning` was hardcoded #FEF3C7/#92400E rather than the gold
   tokens, so it ignored the theme. And the wrapper class was "d-" (nothing)
   instead of "d-inline-flex", so alignment with adjacent text was luck. */
const TONE_ALIASES: Record<string, UiBadgeTone> = {
  default: "neutral", good: "success", ok: "success",
  warn: "warning", error: "danger", critical: "danger",
  // accent used to be a synonym for danger; emphasis is info, not trouble.
  accent: "info",
};

export function Badge({ tone = "neutral", children }: { tone?: UiBadgeTone | keyof typeof TONE_ALIASES; children: ReactNode }) {
  return <UiBadge tone={(TONE_ALIASES[tone as string] ?? tone) as UiBadgeTone} size="sm">{children}</UiBadge>;
}

/* ───────────────────────── Data table ───────────────────────── */
/** Narrow-viewport detector for the table's card mode. Matches the CSS
 *  breakpoint used elsewhere; re-evaluates on resize/rotate. */
function useNarrow(maxWidth = 720): boolean {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== "undefined" && window.matchMedia(`(max-width:${maxWidth}px)`).matches);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width:${maxWidth}px)`);
    const on = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mq.addEventListener("change", on);
    setNarrow(mq.matches);
    return () => mq.removeEventListener("change", on);
  }, [maxWidth]);
  return narrow;
}

export type Col<T> = {
  key: string; label: string; num?: boolean; sortable?: boolean; align?: "left" | "right" | "center";
  render?: (r: T) => ReactNode; sortValue?: (r: T) => string | number;
  /** Card mode (narrow screens): promote this column to the card's headline. */
  cardPrimary?: boolean;
  /** Card mode: show to the right of the headline (status, total). */
  cardMeta?: boolean;
  /** Card mode: omit this column entirely (noise on a phone). */
  cardHide?: boolean;
  /** Value used in the printed/PDF A4 document (falls back to sortValue, then the raw field). */
  docValue?: (r: T) => string | number;
};

export function AdminTable<T extends Record<string, any>>({
  rows, columns, searchKeys, title, toolbar, actions, onRowClick, onAdd, addLabel = "Add", scan = false, onScanCode, empty, minWidth = 640, footer, bare, docTitle, docSubtitle, docSummary, csv = true, columnPicker = true, cards = true,
  renderExpanded, expandLabel = "Details",
  selectable = false, selected, onSelectedChange, rowKey, canSelect,
}: {
  rows: T[] | null;
  /** Row selection for bulk actions. Off by default so existing lists are
   *  untouched. When on, each row gets a checkbox, the header gets select-all
   *  for the visible page, and the caller owns the Set - the table only reports
   *  changes. Keys come from rowKey, or the row's id. This exists so Catalogue's
   *  Activate / Hide / Remove can move onto AdminTable without losing the one
   *  thing the hand-rolled table did that this one did not. */
  selectable?: boolean;
  selected?: Set<string>;
  onSelectedChange?: (next: Set<string>) => void;
  rowKey?: (r: T) => string;
  /** Rows that should not be selectable - already processed, locked, and so
   *  on. Their checkbox is disabled and select-all skips them, so a page-wide
   *  tick never sweeps up something that must not be acted on again. */
  canSelect?: (r: T) => boolean;
  columns: Col<T>[];
  searchKeys?: string[];
  /** Page-specific controls for the header's action slot — the buttons that used
   *  to sit in a band above the card. Utilities the table already owns (CSV,
   *  print, add) do not belong here; passing them again is how Catalogue ended
   *  up with two Add product buttons and two CSV exports. */
  actions?: ReactNode;
  title?: string;
  toolbar?: ReactNode;
  onRowClick?: (r: T) => void;
  /** Expandable row. When set, each row gets a toggle and the returned node is
   *  rendered in a full-width panel underneath it.
   *
   *  This exists because some screens are records, not rows: a transaction
   *  carries six actions, a payments breakdown and a change history. Flattening
   *  that into cells crams six buttons into one column and loses the history.
   *  Expanding underneath keeps the grid — sortable, searchable, exportable —
   *  and gives the record the room it needs. Only ONE row is open at a time:
   *  two open panels and the table stops being scannable, which is the whole
   *  point of it being a table. */
  renderExpanded?: (r: T) => ReactNode;
  /** Accessible name for the toggle, e.g. "Receipt details". */
  expandLabel?: string;
  /** Primary create action, rendered as the last control in the table header so
   *  "add" sits in the same place on every table. */
  onAdd?: () => void;
  addLabel?: string;
  /** Barcode gun support: focuses the search box and reads a whole scan as one
   *  entry (guns type fast then press Enter). */
  scan?: boolean;
  onScanCode?: (code: string) => void;
  empty?: string;
  minWidth?: number;
  footer?: ReactNode[];
  bare?: boolean;
  /** Headline figures printed above the table on the A4 document. */
  docSummary?: { label: string; value: string }[];
  /** Download the filtered rows as CSV. On by default — every table gets it. */
  csv?: boolean;
  /** Let the user hide columns. On by default; the choice is per table and
   *  remembered, because a 14-column table on a laptop is a different table
   *  from the one someone wants on a tablet. */
  columnPicker?: boolean;
  /** When set, a "Print / PDF" action appears that opens the current (filtered + sorted)
   *  rows as a branded A4 document in the in-app viewer — printable / save-as-PDF. */
  docTitle?: string;
  docSubtitle?: string;
  /** Card mode on narrow screens (default on). Set false to force the table —
   *  only for grids where column-to-column comparison is the whole point. */
  cards?: boolean;
}) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<1 | -1>(1);
  const narrow = useNarrow();
  const cardMode = cards && narrow;
  // Hidden columns are remembered per table (keyed on its title), because the
  // set someone wants on a laptop is not the set they want on a tablet, and
  // re-hiding six columns on every visit is how a feature stops being used.
  const prefKey = `epos.cols.${title ?? docTitle ?? "table"}`;
  const [hidden, setHidden] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(prefKey) || "[]")); } catch { return new Set(); }
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const shownCols = columns.filter((c) => !hidden.has(c.key));
  function toggleCol(k: string) {
    setHidden((prev) => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      // Never let the last column be hidden — an empty table is not a view.
      if (n.size >= columns.length) return prev;
      try { localStorage.setItem(prefKey, JSON.stringify([...n])); } catch { /* private mode */ }
      return n;
    });
  }

  const loading = rows === null;
  const all = rows ?? [];
  const needle = q.trim().toLowerCase();
  const keys = searchKeys ?? columns.map((c) => c.key);
  // Reset to the first page when search or sort (criteria that change the visible result set) change.
  useEffect(() => { setPage(0); }, [needle, sortKey, sortDir]);

  const filtered = useMemo(() => {
    let out = !needle ? all : all.filter((r) => keys.some((k) => String((r as any)[k] ?? "").toLowerCase().includes(needle)));
    if (sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      const val = (r: T) => (col?.sortValue ? col.sortValue(r) : (r as any)[sortKey] ?? "");
      out = [...out].sort((a, b) => {
        const va = val(a), vb = val(b);
        if (typeof va === "number" && typeof vb === "number") return (va - vb) * sortDir;
        return String(va).localeCompare(String(vb), undefined, { numeric: true }) * sortDir;
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, needle, sortKey, sortDir]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const p = Math.min(page, pages - 1);
  const slice = filtered.slice(p * pageSize, p * pageSize + pageSize);

  const toggleSort = (k: string) => {
    if (sortKey === k) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortKey(k); setSortDir(1); }
  };

  const Tag: any = bare ? "div" : "section";
  const wrapStyle: CSSProperties = bare
    ? { minWidth: 0 }
    : { minWidth: 0, borderRadius: "var(--r-lg)", border: "1px solid var(--line)", background: "var(--card)", padding: 14 };
  const pageBtnStyle = (disabled: boolean): CSSProperties => ({ borderRadius: 4, background: "var(--paper-2)", padding: "6px 12px", fontSize: 12, color: "var(--ink)", opacity: disabled ? 0.45 : 1 });
  return (
    <Tag style={wrapStyle}>
      {/* A table is a card, and a card has a header. This was one flat wrapping
          flex: title, search, filters, Columns, CSV, Print and the row count all
          on one line, so a table read as a floating toolbar with rows under it
          and pages compensated by drawing their own title above — which is how
          Customers ended up saying "Customers" three times on one screen.
          Two rows now. Identity and the primary action on top, the controls that
          narrow the list beneath. Filters belong here, in the header of the
          thing they filter, not stranded in a band above it. */}
      {(title || onAdd || scan) && (
        <div className="at-head">
          {title ? <h2 className="display at-head-title">{title}</h2> : <span />}
          <span className="num at-count">
            {loading ? "…"
              : filtered.length === 0 ? "0 results"
              : filtered.length <= pageSize ? `${filtered.length} result${filtered.length === 1 ? "" : "s"}`
              : `${p * pageSize + 1}–${Math.min(filtered.length, p * pageSize + pageSize)} of ${filtered.length}`}
          </span>
          <div className="at-head-actions">
            {actions}
            {scan && (
              <button className="fw-bold text-nowrap at-btn" title="Ready to scan" aria-label="Scan a barcode"
                onClick={() => searchRef.current?.focus()}>Scan</button>
            )}
            {onAdd && (
              <button className="fw-bold text-nowrap at-btn at-btn-primary" onClick={onAdd}>+ {addLabel}</button>
            )}
          </div>
        </div>
      )}
      <div className="at-tools">
        {searchKeys !== null && (
          <input ref={searchRef} value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }}
            onKeyDown={(e) => {
              // A barcode gun ends its burst with Enter. Hand the code to the
              // page and clear, so the next scan starts clean.
              if (e.key === "Enter" && onScanCode && q.trim()) { onScanCode(q.trim()); setQ(""); e.preventDefault(); }
            }}
            placeholder={scan ? "Search or scan…" : "Search…"}
            className="flex-grow-1" style={{ height: 34, minWidth: 150, borderRadius: 4, border: "1px solid var(--line)", background: "var(--paper)", padding: "0 12px", fontSize: 13, color: "var(--ink)" }} />
        )}
        {toolbar}
        {/* One cluster, pushed to the trailing edge. These were loose
            children of the row, so flex-wrap spread them and left the slack
            in the middle of the header rather than at its end. */}
        <div className="at-util">
        {columnPicker && !cardMode && (
          <div style={{ position: "relative" }}>
            <button className="fw-bold text-nowrap" aria-expanded={pickerOpen}
              style={{ borderRadius: 4, border: "1px solid var(--line)", background: "var(--paper)", padding: "7px 12px", fontSize: 13, color: "var(--ink)" }}
              onClick={() => setPickerOpen((v) => !v)}>
              Columns{hidden.size ? ` (${columns.length - hidden.size}/${columns.length})` : ""}
            </button>
            {pickerOpen && (
              <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 20, minWidth: 200, maxHeight: 300, overflowY: "auto",
                background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--r-md)", boxShadow: "var(--shadow-md)", padding: 8 }}>
                {columns.map((c) => (
                  <label key={c.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 6px", fontSize: 13, cursor: "pointer" }}>
                    <input type="checkbox" checked={!hidden.has(c.key)} onChange={() => toggleCol(c.key)} />
                    {c.label}
                  </label>
                ))}
                <button style={{ width: "100%", marginTop: 6, padding: "6px", fontSize: 12, borderRadius: 4, background: "var(--paper-2)", color: "var(--ink)" }}
                  onClick={() => { setHidden(new Set()); try { localStorage.removeItem(prefKey); } catch { /* ignore */ } }}>
                  Show all
                </button>
              </div>
            )}
          </div>
        )}
        {csv && (
          <button className="fw-bold text-nowrap"
            style={{ borderRadius: 4, border: "1px solid var(--line)", background: "var(--paper)", padding: "7px 12px", fontSize: 13, color: "var(--ink)" }}
            onClick={() => {
              const cell = (r: T, c: Col<T>) => {
                const v = c.docValue ? c.docValue(r) : c.sortValue ? c.sortValue(r) : (r as any)[c.key];
                return v == null ? "" : String(v);
              };
              // Quote every field: product names contain commas, notes contain
              // newlines, and a half-escaped CSV silently corrupts a column.
              const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
              const body = [shownCols.map((c) => esc(c.label)).join(","),
                ...filtered.map((r) => shownCols.map((c) => esc(cell(r, c))).join(","))].join("\r\n");
              // BOM so Excel opens UTF-8 (£ and accented names) correctly.
              const url = URL.createObjectURL(new Blob(["\uFEFF" + body], { type: "text/csv;charset=utf-8" }));
              const a = document.createElement("a");
              a.href = url;
              a.download = `${(docTitle ?? title ?? "export").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${new Date().toISOString().slice(0, 10)}.csv`;
              a.click(); URL.revokeObjectURL(url);
            }}>
            CSV
          </button>
        )}
        {docTitle && (
          <button
            className="fw-bold text-nowrap"
            style={{ borderRadius: 4, border: "1px solid var(--line)", background: "var(--paper)", padding: "7px 12px", fontSize: 13, color: "var(--ink)" }}
            onClick={async () => {
              const cell = (r: T, c: Col<T>) => {
                const v = c.docValue ? c.docValue(r) : c.sortValue ? c.sortValue(r) : (r as any)[c.key];
                return v == null ? "" : String(v);
              };
              openDocument({ title: docTitle, html: await buildReportHtml({
                title: docTitle, subtitle: docSubtitle,
                columns: shownCols.map((c) => c.label),
                rows: filtered.map((r) => shownCols.map((c) => cell(r, c))),
                summary: docSummary,
                note: needle ? `Filtered by “${needle}”.` : undefined,
              }) });
            }}>
            Print / PDF
          </button>
        )}
        </div>
        {!(title || onAdd || scan) && (
          <span className="num text-nowrap fw-semibold t-meta">
            {loading ? "…"
              : filtered.length === 0 ? "0 results"
              : filtered.length <= pageSize ? `${filtered.length} result${filtered.length === 1 ? "" : "s"}`
              : `${p * pageSize + 1}–${Math.min(filtered.length, p * pageSize + pageSize)} of ${filtered.length}`}
          </span>
        )}
      </div>
      {cardMode ? (
        /* Card mode — on a phone a 9-column table is unreadable at any zoom.
           Same rows, same order, same page: one card each, headline + meta on
           top, remaining fields as label/value lines. Empty values are dropped
           so cards stay short. */
        <div className="d-grid" style={{ gap: 10, minWidth: 0 }}>
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{ border: "1px solid var(--line)", borderRadius: "var(--r-md)", background: "var(--card)", padding: 12 }}>
                <div className="skeleton" style={{ height: 15, width: "55%", borderRadius: 4, marginBottom: 8 }} />
                <div className="skeleton" style={{ height: 12, borderRadius: 4 }} />
              </div>
            ))
          ) : slice.length === 0 ? (
            <EmptyState pad={20} title={needle ? "No matches" : (empty ?? "Nothing to show")} hint={needle ? `Nothing matched “${needle}”.` : undefined} />
          ) : slice.map((r, i) => {
            const shown = columns.filter((c) => !c.cardHide);
            const rid = String((r as any).id ?? i);
            const open = renderExpanded ? openRow === rid : false;
            const primary = shown.find((c) => c.cardPrimary) ?? shown[0];
            const metas = shown.filter((c) => c.cardMeta && c !== primary);
            const rest = shown.filter((c) => c !== primary && !metas.includes(c));
            const cell = (c: Col<T>) => (c.render ? c.render(r) : (r as any)[c.key]);
            const isEmpty = (c: Col<T>) => {
              const v = (r as any)[c.key];
              return !c.render && (v === null || v === undefined || v === "");
            };
            return (
              <div key={(r as any).id ?? i}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                style={{
                  border: "1px solid var(--line)", borderRadius: "var(--r-md)", background: "var(--card)",
                  padding: 12, minWidth: 0, ...(onRowClick ? { cursor: "pointer" } : {}),
                }}>
                <div className="d-flex align-items-start justify-content-between gap-3" >
                  <div className="fw-bold" style={{ minWidth: 0, overflowWrap: "anywhere" }}>{cell(primary)}</div>
                  {metas.length > 0 && (
                    <div className="d-flex align-items-center text-nowrap" style={{ gap: 8, flexShrink: 0 }}>
                      {metas.map((c) => <span key={c.key} className={c.num ? "num fw-semibold" : "fw-semibold"}>{cell(c)}</span>)}
                    </div>
                  )}
                  {/* Same toggle as the table row. Card mode dropped renderExpanded
                      entirely, so on a phone Transactions had no Edit details, Void
                      or View receipt, and Bank had no match panel — the row's
                      actions existed only on screens wide enough for the table. */}
                  {renderExpanded && (
                    <button className="admin-expand flex-shrink-0" aria-expanded={open} aria-label={`${expandLabel} — ${open ? "hide" : "show"}`}
                      onClick={(e) => { e.stopPropagation(); setOpenRow(open ? null : rid); }}>
                      <span aria-hidden style={{ display: "inline-block", transition: "transform .15s ease", transform: open ? "rotate(90deg)" : "none" }}>›</span>
                    </button>
                  )}
                </div>
                {rest.filter((c) => !isEmpty(c)).length > 0 && (
                  <div className="d-grid" style={{ gap: 4, marginTop: 8 }}>
                    {rest.filter((c) => !isEmpty(c)).map((c) => (
                      <div key={c.key} className="d-flex align-items-baseline justify-content-between" style={{ gap: 12, fontSize: 13 }}>
                        <span style={{ color: "var(--muted)", flexShrink: 0 }}>{c.label}</span>
                        <span className={c.num ? "num" : undefined} style={{ textAlign: "right", minWidth: 0, overflowWrap: "anywhere" }}>{cell(c)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {open && (
                  <div className="admin-card-expanded" onClick={(e) => e.stopPropagation()}>
                    <div className="admin-card-expanded-head">
                      <span className="t-meta">{expandLabel}</span>
                      <div className="recnav">
                        <button type="button" aria-label="Previous row" title="Previous row" disabled={i <= 0}
                          onClick={() => setOpenRow(String((slice[i - 1] as any)?.id ?? ""))}>‹</button>
                        <span className="recnav-pos" aria-live="polite">{p * pageSize + i + 1} of {filtered.length}</span>
                        <button type="button" aria-label="Next row" title="Next row" disabled={i >= slice.length - 1}
                          onClick={() => setOpenRow(String((slice[i + 1] as any)?.id ?? ""))}>›</button>
                      </div>
                    </div>
                    {renderExpanded!(r)}
                  </div>
                )}
              </div>
            );
          })}
          {footer && !loading && !needle && filtered.length > 0 && (
            <div style={{ border: "1px solid var(--line)", borderRadius: "var(--r-md)", background: "var(--paper-2)", padding: 12 }}>
              <div className="d-grid" style={{ gap: 4, fontSize: 13, fontWeight: 800 }}>
                {footer.map((cellVal, i) => cellVal == null || cellVal === "" ? null : (
                  <div key={i} className="d-flex align-items-baseline justify-content-between" style={{ gap: 12 }}>
                    <span className="t-muted">{columns[i]?.label}</span>
                    <span className={columns[i]?.num ? "num" : undefined}>{cellVal}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
      <div className="table-scroll" style={{ minWidth: 0, borderRadius: 4, border: "1px solid var(--line)" }}>
        <table className="data-table w-100" style={{ minWidth }}>
          <thead>
            <tr>
              {selectable && (() => {
                const keyOf = (r: T) => rowKey ? rowKey(r) : String((r as any).id ?? "");
                const pageKeys = slice.filter((r) => !canSelect || canSelect(r)).map(keyOf).filter(Boolean);
                const sel = selected ?? new Set<string>();
                const allOn = pageKeys.length > 0 && pageKeys.every((k) => sel.has(k));
                const someOn = !allOn && pageKeys.some((k) => sel.has(k));
                return (
                  <th className="position-sticky top-0" style={{ zIndex: 1, background: "var(--card)", width: 1 }}>
                    <input type="checkbox" aria-label="Select all on this page" checked={allOn}
                      ref={(el) => { if (el) el.indeterminate = someOn; }}
                      onChange={() => {
                        const next = new Set(sel);
                        if (allOn) pageKeys.forEach((k) => next.delete(k)); else pageKeys.forEach((k) => next.add(k));
                        onSelectedChange?.(next);
                      }} />
                  </th>
                );
              })()}
              {shownCols.map((c) => (
                <th key={c.key} className={`position-sticky top-0 ${c.num ? "num" : ""}`}
                  style={{ zIndex: 1, background: "var(--card)", textAlign: c.align ?? (c.num ? "right" : "left"), ...(c.sortable ? { cursor: "pointer", userSelect: "none" } : {}) }}
                  onClick={c.sortable ? () => toggleSort(c.key) : undefined}>
                  {c.label || <span className="sr-only">Actions</span>}{c.sortable && sortKey === c.key ? (sortDir === 1 ? " ▲" : " ▼") : ""}
                </th>
              ))}
              {/* Text, not just aria-label: a header with no text is announced as an empty column. */}
              {renderExpanded && <th style={{ width: 1 }}><span className="sr-only">{expandLabel}</span></th>}
              {onRowClick && <th style={{ width: 1 }}><span className="sr-only">Actions</span></th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>{shownCols.map((c) => <td key={c.key}><div className="skeleton" style={{ height: 14, borderRadius: 4 }} /></td>)}</tr>
              ))
            ) : slice.length === 0 ? (
              <tr><td colSpan={shownCols.length + (onRowClick ? 1 : 0) + (renderExpanded ? 1 : 0) + (selectable ? 1 : 0)} className="px-2" style={{ paddingTop: 16, paddingBottom: 16 }}><EmptyState pad={20} title={needle ? "No matches" : (empty ?? "Nothing to show")} hint={needle ? `Nothing matched “${needle}”.` : undefined} /></td></tr>
            ) : (
              slice.map((r, i) => {
                const rid = String((r as any).id ?? i);
                const open = renderExpanded ? openRow === rid : false;
                const rowIndex = i;
                return (
                <Fragment key={rid}>
                <tr onClick={onRowClick ? () => onRowClick(r) : undefined} style={onRowClick ? { cursor: "pointer" } : undefined}>
                  {selectable && (() => {
                    const k = rowKey ? rowKey(r) : String((r as any).id ?? "");
                    const on = !!selected?.has(k);
                    const allowed = !canSelect || canSelect(r);
                    return (
                      <td style={{ width: 1 }} onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" aria-label={allowed ? "Select row" : "Already processed"} checked={on} disabled={!allowed}
                          title={allowed ? undefined : "Already matched"}
                          onChange={() => { const next = new Set(selected ?? []); on ? next.delete(k) : next.add(k); onSelectedChange?.(next); }} />
                      </td>
                    );
                  })()}
                  {shownCols.map((c) => <td key={c.key} className={c.num ? "num" : undefined} style={{ textAlign: c.align ?? (c.num ? "right" : "left") }}>{c.render ? c.render(r) : (r as any)[c.key]}</td>)}
                  {renderExpanded && (
                    <td style={{ width: 1, whiteSpace: "nowrap" }}>
                      <button className="admin-expand" aria-expanded={open} aria-label={`${expandLabel} — ${open ? "hide" : "show"}`}
                        onClick={(e) => { e.stopPropagation(); setOpenRow(open ? null : rid); }}>
                        <span aria-hidden style={{ display: "inline-block", transition: "transform .15s ease", transform: open ? "rotate(90deg)" : "none" }}>›</span>
                      </button>
                    </td>
                  )}
                  {onRowClick && (
                    <td className="num" style={{ width: 1, whiteSpace: "nowrap" }}>
                      <button className="fw-bold" aria-label="View row"
                        style={{ borderRadius: 4, border: "1px solid var(--line)", background: "var(--paper-2)", padding: "5px 11px", fontSize: 12, color: "var(--ink)" }}
                        onClick={(e) => { e.stopPropagation(); onRowClick(r); }}>View</button>
                    </td>
                  )}
                </tr>
                {open && (
                  <tr>
                    <td colSpan={shownCols.length + (onRowClick ? 1 : 0) + 1} style={{ background: "var(--paper-2)", padding: "14px 16px" }}>
                      {/* Step through records without collapsing back to the
                          list. Opening row 5 of 40 and wanting row 6 meant
                          closing, finding it, opening it again — three actions
                          to move one row. The position is shown because "next"
                          is only useful if you know how much is left.
                          Sorted order, not id order: it follows what is on the
                          screen, including the current sort and filter. */}
                      <div className="d-flex align-items-center justify-content-between" style={{ gap: 12, marginBottom: 12 }}>
                        <span className="t-meta">{expandLabel}</span>
                        <div className="recnav">
                          <button type="button" aria-label="Previous row" title="Previous row"
                            disabled={rowIndex <= 0}
                            onClick={() => setOpenRow(String((slice[rowIndex - 1] as any)?.id ?? ""))}>‹</button>
                          <span className="recnav-pos" aria-live="polite">{p * pageSize + rowIndex + 1} of {filtered.length}</span>
                          <button type="button" aria-label="Next row" title="Next row"
                            disabled={rowIndex < 0 || rowIndex >= slice.length - 1}
                            onClick={() => setOpenRow(String((slice[rowIndex + 1] as any)?.id ?? ""))}>›</button>
                        </div>
                      </div>
                      {renderExpanded!(r)}
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })
            )}
          </tbody>
          {footer && !loading && !needle && filtered.length > 0 && (
            <tfoot>
              <tr className="fw-800">
                {footer.map((cell, i) => (
                  <td key={i} className={columns[i]?.num ? "num" : undefined} style={{ borderTop: "2px solid var(--line)", background: "var(--paper-2)", textAlign: columns[i]?.align ?? (columns[i]?.num ? "right" : "left") }}>{cell}</td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      )}
      <div className="d-flex flex-wrap align-items-center justify-content-between" style={{ marginTop: 12, gap: 10 }}>
        <label className="d-flex align-items-center fw-semibold" style={{ gap: 6, fontSize: 12, color: "var(--muted)" }}>
          Rows
          <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
            style={{ borderRadius: 4, border: "1px solid var(--line)", background: "var(--paper)", padding: "4px 8px", fontSize: 12, color: "var(--ink)" }}>
            {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <div className="d-flex align-items-center gap-3" >
          <button className="fw-bold" style={pageBtnStyle(p <= 0)} disabled={p <= 0} onClick={() => setPage(p - 1)}>‹ Prev</button>
          <span className="num fw-semibold t-meta" >Page {p + 1} of {pages}</span>
          <button className="fw-bold" style={pageBtnStyle(p >= pages - 1)} disabled={p >= pages - 1} onClick={() => setPage(p + 1)}>Next ›</button>
        </div>
      </div>
    </Tag>
  );
}

/* ───────────────────────── Page wrapper ───────────────────────── */
// The page wrapper. Every geometric value lives in `.bo-page` in global.css —
// the grid track, the band gap, the vertical padding and the per-device rhythm.
// They were inline here, which meant no media query could reach them, so the
// page had one rhythm on a 27" monitor and on a phone. Read that block before
// changing spacing; it records why each value is what it is.
//
// This component deliberately takes no style prop and no arbitrary className. A
// page that needs a different shape picks a named `width` below; it does not
// invent one here — that is how the previous divergence started.
export function PageBody({ children, width = "full" }: {
  children: ReactNode;
  /** "full" fills the column. "read" caps at 72ch for pages that are read as
   *  prose rather than scanned as data — prose at 1680px loses the line. */
  width?: "full" | "read";
}) {
  return <div className={width === "read" ? "bo-page bo-page-read" : "bo-page"}>{children}</div>;
}
